/**
 * useCalibrationCheck - the ASTM C1074 9.2 check for a planned pour: asks
 * the server (POST /pour/calibration-check) whether a pour made with this mix
 * design and calibration would compute maturity on the same basis as the
 * calibration was developed with. Without a calibration the server checks the
 * one the pour would get (its own pick) and names it in the result.
 *
 * Runs again whenever the mix design or calibration changes (only while
 * `enabled`), drops answers to an earlier pick, and clears the user's
 * confirmation of a 'differs' result so each pick is confirmed on its own.
 *
 * A save the server refuses on these rules (applySaveRefusal) becomes the
 * check shown, so it can be settled on the page: a difference gets its
 * confirmation checkbox, a mismatch holds the save, and a calibration that is
 * no longer the server's pick is checked again. The rule refuses only a pour
 * that computes maturity, so a refusal made while the pour computed maturity
 * stops holding once it no longer does (refusalHolds).
 */
import { useCallback, useEffect, useState } from 'react';
import { poursAPI, type CalibrationCheck } from '../../services/pours';
import { calibrationRefusal } from '../../utils/calibrationCheck';

export interface CalibrationCheckState {
  check: CalibrationCheck | null;
  loading: boolean;
  /** The request failed; the server still checks on save. */
  failed: boolean;
  /** The check is the server's answer to a save (a 422), not the check endpoint's. */
  fromSave: boolean;
  /** That answer still holds the save: the server's answer counts whatever
   *  the page thought, but a refusal made while the pour computed maturity
   *  lapses once the pour no longer does. */
  refusalHolds: boolean;
  /** The user ticked the 'differs' confirmation for this check. */
  acknowledged: boolean;
  setAcknowledged: (value: boolean) => void;
  /** Takes a save's 422 detail text; true when it was a refusal on the
   *  calibration rules (then shown as the check, or checked again). */
  applySaveRefusal: (detail: string) => boolean;
  /** Forgets the check and any refusal (e.g. once a save went through), and
   *  asks again while enabled. */
  reset: () => void;
}

export const useCalibrationCheck = (
  productId: string | null | undefined,
  calibrationId: string | null | undefined,
  enabled: boolean,
  /** Whether the pour, as it stands on the page, computes maturity. */
  computesMaturity: boolean,
): CalibrationCheckState => {
  const [check, setCheck] = useState<CalibrationCheck | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [fromSave, setFromSave] = useState(false);
  // The pour computed maturity when the save was refused.
  const [refusedWhileMaturity, setRefusedWhileMaturity] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  // Bumped to ask the server again for the same pick.
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    setAcknowledged(false);
    setCheck(null);
    setFailed(false);
    setFromSave(false);
    setRefusedWhileMaturity(false);
    if (!enabled || !productId) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    // Promise.resolve().then: a throw while starting the request is handled
    // like a failed request.
    Promise.resolve()
      .then(() => poursAPI.checkCalibration(productId, calibrationId || null))
      .then((result) => {
        if (cancelled) return;
        setCheck(result ?? null);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setFailed(true);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [productId, calibrationId, enabled, refreshKey]);

  const applySaveRefusal = useCallback((detail: string): boolean => {
    const refusal = calibrationRefusal(detail);
    if (!refusal) return false;
    if (refusal.status === 'not_active') {
      setRefreshKey((n) => n + 1);
      return true;
    }
    // The server's own words: what it computed, whatever the check said.
    setCheck((prev) => ({
      status: refusal.status,
      differences: prev?.status === refusal.status ? prev.differences : [],
      pour_basis: prev?.status === refusal.status ? prev.pour_basis : null,
      calibration_basis: prev?.status === refusal.status ? prev.calibration_basis : null,
      calibration_id: prev?.calibration_id ?? null,
      calibration_name: prev?.calibration_name ?? null,
      text: refusal.text,
    }));
    setAcknowledged(false);
    setFailed(false);
    setLoading(false);
    setFromSave(true);
    setRefusedWhileMaturity(computesMaturity);
    return true;
  }, [computesMaturity]);

  const reset = useCallback(() => setRefreshKey((n) => n + 1), []);

  const refusalHolds = fromSave && (computesMaturity || !refusedWhileMaturity);

  return {
    check, loading, failed, fromSave, refusalHolds, acknowledged, setAcknowledged, applySaveRefusal, reset,
  };
};

export default useCalibrationCheck;
